import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { WarningCircleIcon, GlobeIcon } from "@phosphor-icons/react";
import { CellData } from "../types";

const CARD_WIDTH = 320;
const GAP = 6;
const VIEWPORT_MARGIN = 8;
const MAX_SOURCES = 4;

interface CellHoverCardProps {
  anchor: DOMRect;
  header: string;
  cell: CellData;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const hostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const CellHoverCard: React.FC<CellHoverCardProps> = ({ anchor, header, cell, onMouseEnter, onMouseLeave }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; above: boolean } | null>(null);

  // Renders off-screen first, then places the card below the cell, or above when it would overflow.
  useLayoutEffect(() => {
    const height = cardRef.current?.offsetHeight ?? 0;
    const fitsBelow = anchor.bottom + GAP + height <= window.innerHeight - VIEWPORT_MARGIN;
    const top = fitsBelow ? anchor.bottom + GAP : Math.max(VIEWPORT_MARGIN, anchor.top - GAP - height);
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, anchor.left),
      window.innerWidth - CARD_WIDTH - VIEWPORT_MARGIN
    );
    setPosition({ top, left, above: !fitsBelow });
  }, [anchor, cell]);

  const sources = (cell.sources ?? []).filter((source) => source?.url);
  const visibleSources = sources.slice(0, MAX_SOURCES);

  return createPortal(
    <motion.div
      ref={cardRef}
      role="tooltip"
      className="cell-card"
      style={{
        width: CARD_WIDTH,
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? "visible" : "hidden",
      }}
      initial={{ opacity: 0, y: position?.above ? 4 : -4 }}
      animate={position ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.14, ease: "easeOut" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="cell-card-header">
        <span className="cell-card-label">{header || "Untitled column"}</span>
      </div>

      {cell.status === "failed" ? (
        <div className="cell-card-status is-failed">
          <WarningCircleIcon size={14} aria-hidden="true" />
          <div>
            <p className="cell-card-status-title">{cell.error || "Research failed"}</p>
            <p className="cell-card-status-text">This cell is still empty. Use Retry failed below the table to run it again.</p>
          </div>
        </div>
      ) : cell.status === "not_found" ? (
        <div className="cell-card-status">
          <div>
            <p className="cell-card-status-title">No answer found</p>
            <p className="cell-card-status-text">
              Research didn't find a public value. Run the table again to retry this cell.
            </p>
          </div>
        </div>
      ) : (
        <p className="cell-card-value">{cell.value}</p>
      )}

      {visibleSources.length > 0 && (
        <div className="cell-card-sources">
          <p className="cell-card-sources-label">
            Sources <span>{sources.length}</span>
          </p>
          <ul>
            {visibleSources.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noopener noreferrer">
                  {source.favicon ? (
                    <img src={source.favicon} alt="" width={14} height={14} />
                  ) : (
                    <GlobeIcon size={14} aria-hidden="true" />
                  )}
                  <span className="cell-card-source-title">{source.title || hostname(source.url)}</span>
                  <span className="cell-card-source-host">{hostname(source.url)}</span>
                </a>
              </li>
            ))}
          </ul>
          {sources.length > MAX_SOURCES && (
            <p className="cell-card-more">+{sources.length - MAX_SOURCES} more</p>
          )}
        </div>
      )}
    </motion.div>,
    document.body
  );
};

export default CellHoverCard;
